
import React, { useState } from 'react';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface CompanyChatWidgetProps {
  companyName: string;
  onClose?: () => void;
}

const CompanyChatWidget = ({ companyName, onClose }: CompanyChatWidgetProps) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const { toast } = useToast();
  const { user, session } = useAuth();

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    if (!user || !session) {
      toast({
        title: "Authentication required",
        description: "Please log in to use the chat advisor.",
        variant: "destructive"
      });
      return;
    }

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const messageContent = input;
    setInput('');
    setIsLoading(true);

    console.log('Sending chat message:', { companyName, message: messageContent, sessionId });

    try {
      // Use the current session directly instead of getting a fresh one
      if (!session?.access_token) {
        throw new Error('No valid access token found');
      }

      console.log('Using session for chat request:', { 
        hasAccessToken: !!session.access_token,
        userId: session.user?.id 
      });

      const { data, error } = await supabase.functions.invoke('company-chat', {
        body: {
          companyName,
          message: messageContent,
          sessionId
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        }
      });

      console.log('Chat response:', { data, error });

      if (error) {
        console.error('Supabase function error:', error);
        throw new Error(error.message || 'Failed to get response from chat service');
      }

      if (!data) {
        throw new Error('No response data received');
      }

      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: data.response || 'Sorry, I could not generate a response.',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
      
      if (data.sessionId && !sessionId) {
        setSessionId(data.sessionId);
      }

    } catch (error: any) {
      console.error('Chat error details:', error);
      
      // Remove the user message if there was an error
      setMessages(prev => prev.filter(msg => msg.id !== userMessage.id));
      
      let errorMessage = "Failed to get response. Please try again.";
      
      if (error.message?.includes('Authentication') || error.message?.includes('401')) {
        errorMessage = "Authentication error. Please try logging out and back in.";
      } else if (error.message?.includes('network') || error.message?.includes('fetch')) {
        errorMessage = "Network error. Please check your connection and try again.";
      }
      
      toast({
        title: "Chat error",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <Card className="fixed bottom-6 right-6 w-96 h-[500px] shadow-lg z-50 flex flex-col">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 bg-redflags-red text-white rounded-t-lg">
        <CardTitle className="text-lg font-semibold">
          {companyName} Advisor
        </CardTitle>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="text-white hover:bg-white/20"
        >
          <X className="w-4 h-4" />
        </Button>
      </CardHeader>
      
      <CardContent className="flex-1 flex flex-col p-0">
        <ScrollArea className="flex-1 p-4">
          {messages.length === 0 ? (
            <div className="text-center text-gray-500 mt-8">
              <MessageCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-sm">
                Ask me anything about {companyName}!
              </p>
              <p className="text-xs mt-2">
                I can help with job security, culture, finances, and more.
              </p>
              {!user && (
                <p className="text-xs mt-2 text-orange-600">
                  Please log in to use the chat feature.
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
                      message.role === 'user'
                        ? 'bg-redflags-red text-white'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {message.content}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 rounded-lg px-3 py-2 text-sm flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Analyzing data...
                  </div>
                </div>
              )}
            </div>
          )}
        </ScrollArea>
        
        <div className="p-4 border-t">
          <div className="flex gap-2">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={user ? "Ask about this company..." : "Please log in to chat"}
              disabled={isLoading || !user}
              className="flex-1"
            />
            <Button
              onClick={sendMessage}
              disabled={isLoading || !input.trim() || !user}
              size="icon"
              className="bg-redflags-red hover:bg-redflags-red/90"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default CompanyChatWidget;
